import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchSelect } from "@/components/SearchSelect";
import { COUNTRIES, US_STATES, countryByIso } from "@/lib/countries";

const inputCls = "rounded-none h-12 border-ink/20 focus-visible:ring-flame";
const labelCls = "text-xs font-bold uppercase tracking-widest";

// Display mirrors backend DISCLOSURE_VERSION / AUTHORIZATION_VERSION (server is source of truth on save)
const DISCLOSURE_VERSION = "v0.1-draft-legal-review-required";
const AUTHORIZATION_VERSION = "v0.1-draft-legal-review-required";

const FieldErr = ({ msg, testid }) =>
  msg ? <p className="text-xs font-semibold text-red-600" data-testid={testid}>{msg}</p> : null;

const countryOptions = COUNTRIES.map((c) => ({ id: c.iso, search: c.search, ...c }));
const stateOptions = US_STATES.map((s) => ({ id: s, search: s.toLowerCase(), name: s }));
const countryNameOptions = COUNTRIES.map((c) => ({ id: c.name, search: c.search, name: c.name }));

const formatDob = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const dobError = (v) => {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return "Enter your date of birth as MM/DD/YYYY.";
  const [mm, dd, yyyy] = v.split("/").map(Number);
  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd)
    return "That date doesn't exist — check the month and day.";
  const today = new Date();
  let age = today.getFullYear() - yyyy;
  if (today.getMonth() + 1 < mm || (today.getMonth() + 1 === mm && today.getDate() < dd)) age -= 1;
  if (age < 18 || age > 45) return "Roamies trips are for travelers ages 18–45.";
  return "";
};

const digits = (v) => v.replace(/\D/g, "");

export const ApplicationForm = ({ trip }) => {
  const blank = {
    trip_slug: trip.slug, first_name: "", middle_name: "", last_name: "", dob: "",
    email: "", phone: "", phone_iso: "US", city: "", state: "", country: "", address: "",
    emergency_contact_name: "", emergency_contact_relationship: "", emergency_contact_phone: "", ec_phone_iso: "US",
    consent: false,
  };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setErrors((er) => ({ ...er, [k]: "" }));
  };
  const setVal = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required.";
    if (!form.last_name.trim()) e.last_name = "Required.";
    const dErr = dobError(form.dob);
    if (dErr) e.dob = dErr;
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Enter a valid email address.";
    if (!form.city.trim()) e.city = "Required.";
    if (!form.country) e.country = "Select your country.";
    if (!form.state.trim()) e.state = form.country === "United States" ? "Select your state." : "Required.";
    if (digits(form.emergency_contact_phone) && !form.ec_phone_iso) e.ec_phone = "Choose a calling code.";
    if (!form.consent) e.consent = "Please read the disclosure and check the authorization box to apply.";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) {
      toast.error("Please fix the highlighted fields and try again.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/applications", {
        trip_slug: form.trip_slug,
        first_name: form.first_name.trim(), middle_name: form.middle_name.trim(), last_name: form.last_name.trim(),
        dob: form.dob, email: form.email.trim(),
        phone: form.phone, phone_code: countryByIso(form.phone_iso)?.dial || "+1",
        city: form.city.trim(), state: form.state.trim(), country: form.country, address: form.address.trim(),
        emergency_contact_name: form.emergency_contact_name.trim(),
        emergency_contact_relationship: form.emergency_contact_relationship.trim(),
        emergency_contact_phone: form.emergency_contact_phone,
        emergency_contact_phone_code: countryByIso(form.ec_phone_iso)?.dial || "",
        consent_authorization: form.consent,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="application-success">
        <p className="font-display text-3xl uppercase">Application received.</p>
        <p className="mt-4 text-sm text-ink/70 leading-relaxed max-w-sm mx-auto">
          Your screening authorization is on file. Every application is reviewed before a spot is confirmed —
          no payment is collected at this stage. Check your inbox for a confirmation email.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="application-form" className="space-y-5" noValidate>
      <p className="text-xs text-ink/60 leading-relaxed border-l-2 border-flame pl-3">
        Booking is open for this trip. Every application is reviewed before a spot is confirmed —
        no payment is collected at this stage.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ap-first" className={labelCls}>Legal first name</Label>
          <Input id="ap-first" value={form.first_name} onChange={set("first_name")} data-testid="application-first-name"
            className={`${inputCls} ${errors.first_name ? "border-red-500" : ""}`} />
          <FieldErr msg={errors.first_name} testid="application-first-name-error" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ap-middle" className={labelCls}>Middle <span className="text-ink/40 normal-case font-medium">(optional)</span></Label>
          <Input id="ap-middle" value={form.middle_name} onChange={set("middle_name")} data-testid="application-middle-name" className={inputCls} />
        </div>
        <div className="space-y-2 col-span-2 sm:col-span-1">
          <Label htmlFor="ap-last" className={labelCls}>Legal last name</Label>
          <Input id="ap-last" value={form.last_name} onChange={set("last_name")} data-testid="application-last-name"
            className={`${inputCls} ${errors.last_name ? "border-red-500" : ""}`} />
          <FieldErr msg={errors.last_name} testid="application-last-name-error" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ap-dob" className={labelCls}>Date of birth</Label>
          <Input id="ap-dob" inputMode="numeric" value={form.dob}
            onChange={(e) => setVal("dob", formatDob(e.target.value))}
            data-testid="application-dob" placeholder="MM/DD/YYYY"
            className={`${inputCls} ${errors.dob ? "border-red-500" : ""}`} />
          <FieldErr msg={errors.dob} testid="application-dob-error" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ap-email" className={labelCls}>Email</Label>
          <Input id="ap-email" type="email" value={form.email} onChange={set("email")} data-testid="application-email"
            className={`${inputCls} ${errors.email ? "border-red-500" : ""}`} />
          <FieldErr msg={errors.email} testid="application-email-error" />
        </div>
      </div>

      <div className="space-y-2">
        <Label className={labelCls}>Phone <span className="text-ink/40 normal-case font-medium">(optional)</span></Label>
        <div className="flex gap-2">
          <div className="w-36 sm:w-44 shrink-0">
            <SearchSelect selectedIso={form.phone_iso} onSelect={(v) => setVal("phone_iso", v)}
              placeholder="Code" testid="application-phone-code" options={countryOptions}
              renderSelected={(o) => <span>{o.dial}</span>}
              renderOption={(o) => <span className="flex justify-between w-full gap-2"><span className="truncate">{o.name}</span><span className="text-ink/40 shrink-0">{o.dial}</span></span>} />
          </div>
          <Input type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="application-phone"
            placeholder="555 123 4567" className={`${inputCls} flex-1 ${errors.phone ? "border-red-500" : ""}`} />
        </div>
        <FieldErr msg={errors.phone} testid="application-phone-error" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ap-city" className={labelCls}>City</Label>
          <Input id="ap-city" value={form.city} onChange={set("city")} data-testid="application-city"
            className={`${inputCls} ${errors.city ? "border-red-500" : ""}`} />
          <FieldErr msg={errors.city} testid="application-city-error" />
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Country</Label>
          <SearchSelect selectedIso={form.country} onSelect={(v) => { setVal("country", v); if (v !== "United States") setVal("state", ""); }}
            placeholder="Select country" testid="application-country" options={countryNameOptions} hasError={!!errors.country}
            renderSelected={(o) => <span>{o.name}</span>}
            renderOption={(o) => <span>{o.name}</span>} />
          <FieldErr msg={errors.country} testid="application-country-error" />
        </div>
      </div>

      <div className="space-y-2">
        <Label className={labelCls}>{form.country === "United States" ? "State" : "State / Province / Region"}</Label>
        {form.country === "United States" ? (
          <SearchSelect selectedIso={form.state} onSelect={(v) => setVal("state", v)}
            placeholder="Select state" testid="application-state" options={stateOptions} hasError={!!errors.state}
            renderSelected={(o) => <span>{o.name}</span>}
            renderOption={(o) => <span>{o.name}</span>} />
        ) : (
          <Input value={form.state} onChange={set("state")} data-testid="application-state"
            placeholder={form.country ? "State, province or region" : "Select a country first"}
            className={`${inputCls} ${errors.state ? "border-red-500" : ""}`} />
        )}
        <FieldErr msg={errors.state} testid="application-state-error" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ap-address" className={labelCls}>Current address <span className="text-ink/40 normal-case font-medium">(optional — used for screening only)</span></Label>
        <Input id="ap-address" value={form.address} onChange={set("address")} data-testid="application-address" className={inputCls} placeholder="Street, apt" />
      </div>

      <div className="border-t border-ink/10 pt-5">
        <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">Emergency contact <span className="text-ink/40 normal-case font-medium">(optional)</span></p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ap-ec-name" className={labelCls}>Name <span className="text-ink/40 normal-case font-medium">(optional)</span></Label>
            <Input id="ap-ec-name" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} data-testid="application-ec-name" className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ap-ec-rel" className={labelCls}>Relationship <span className="text-ink/40 normal-case font-medium">(optional)</span></Label>
            <Input id="ap-ec-rel" value={form.emergency_contact_relationship} onChange={set("emergency_contact_relationship")} data-testid="application-ec-relationship" placeholder="e.g. Sister, partner" className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4">
          <div className="space-y-2">
            <Label className={labelCls}>Phone <span className="text-ink/40 normal-case font-medium">(optional)</span></Label>
            <div className="flex gap-2">
              <div className="w-36 sm:w-44 shrink-0">
                <SearchSelect selectedIso={form.ec_phone_iso} onSelect={(v) => setVal("ec_phone_iso", v)}
                  placeholder="Code" testid="application-ec-phone-code" options={countryOptions}
                  renderSelected={(o) => <span>{o.dial}</span>}
                  renderOption={(o) => <span className="flex justify-between w-full gap-2"><span className="truncate">{o.name}</span><span className="text-ink/40 shrink-0">{o.dial}</span></span>} />
              </div>
              <Input type="tel" inputMode="tel" value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")}
                data-testid="application-ec-phone" placeholder="555 123 4567"
                className={`${inputCls} flex-1 ${errors.ec_phone ? "border-red-500" : ""}`} />
            </div>
            <FieldErr msg={errors.ec_phone} testid="application-ec-phone-error" />
          </div>
        </div>
      </div>

      {/* SCREENING DISCLOSURE & AUTHORIZATION — required before submit */}
      <div className="border border-ink/15 p-5 space-y-4" data-testid="application-screening-consent">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame">Background Screening Disclosure &amp; Authorization</p>
        <div className="text-xs text-ink/70 leading-relaxed space-y-2">
          <p>
            Roamies may request an identity and background screening through a legitimate third-party screening
            provider as part of reviewing your application. Roamies does not perform background searches itself,
            and detailed reports stay inside the provider's secure dashboard — Roamies receives only a status and reference.
          </p>
          <p>
            Submitting an application does not guarantee acceptance or a confirmed booking. Any potential match
            is reviewed by a human before any decision is made.
          </p>
          <p>
            We never ask for your SSN or ID documents here. If a screening provider needs those details,
            you will enter them through the provider's own secure workflow.
          </p>
          <p className="text-[10px] text-ink/45">
            Disclosure {DISCLOSURE_VERSION} · Authorization {AUTHORIZATION_VERSION}
          </p>
        </div>
        <label className="flex items-start gap-3 text-sm font-semibold cursor-pointer" data-testid="application-consent-label">
          <Checkbox checked={form.consent} onCheckedChange={(v) => setVal("consent", !!v)} data-testid="application-consent-checkbox"
            className={`rounded-none mt-0.5 data-[state=checked]:bg-flame data-[state=checked]:border-flame ${errors.consent ? "border-red-500" : "border-ink/30"}`} />
          I have read this disclosure and I authorize Roamies to request a screening from a third-party provider as part of my application.
        </label>
        <FieldErr msg={errors.consent} testid="application-consent-error" />
      </div>

      <Button type="submit" disabled={loading} data-testid="application-submit-btn"
        className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-14 font-bold uppercase tracking-[0.2em] text-sm transition-transform hover:-translate-y-0.5">
        {loading ? "Submitting..." : "Submit Application"}
      </Button>
    </form>
  );
};
